import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface StealthModeConfig {
  // Balance hiding
  hideBalances: boolean;
  hideTotalValue: boolean;
  hideIndividualBalances: boolean;

  // Transaction hiding
  hideTransactionHistory: boolean;
  hideRecentTransactions: boolean;

  // Portfolio hiding
  hidePortfolioChart: boolean;
  hideAssetList: boolean;
  hidePnL: boolean;

  // Activity hiding
  hideStakingRewards: boolean;
  hideWhaleAlerts: boolean;
  hideDCAStatus: boolean;

  // Auto-hide settings
  autoHideAfterSeconds: number; // 0 = disabled
  hideOnScreenshot: boolean;

  // Quick toggle
  isEnabled: boolean;
}

interface StealthModeContextType {
  config: StealthModeConfig;
  isStealthMode: boolean;
  toggleStealthMode: () => void;
  updateConfig: (updates: Partial<StealthModeConfig>) => void;
  showValueTemporarily: (durationMs?: number) => void;
  formatHiddenValue: (value: string | number) => string;
  shouldHide: (field: keyof StealthModeConfig) => boolean;
}

const defaultConfig: StealthModeConfig = {
  hideBalances: true,
  hideTotalValue: true,
  hideIndividualBalances: true,
  hideTransactionHistory: false,
  hideRecentTransactions: true,
  hidePortfolioChart: false,
  hideAssetList: false,
  hidePnL: true,
  hideStakingRewards: true,
  hideWhaleAlerts: false,
  hideDCAStatus: true,
  autoHideAfterSeconds: 30,
  hideOnScreenshot: true,
  isEnabled: false,
};

const StealthModeContext = createContext<StealthModeContextType | null>(null);

export function StealthModeProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<StealthModeConfig>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('nardium_stealth_config');
    if (saved) {
      try {
        return { ...defaultConfig, ...JSON.parse(saved) };
      } catch {
        return defaultConfig;
      }
    }
    return defaultConfig;
  });

  const [temporaryShow, setTemporaryShow] = useState(false);
  const [autoHideTimer, setAutoHideTimer] = useState<NodeJS.Timeout | null>(null);

  // Save config to localStorage
  useEffect(() => {
    localStorage.setItem('nardium_stealth_config', JSON.stringify(config));
  }, [config]);

  // Auto-hide timer
  useEffect(() => {
    if (config.isEnabled && config.autoHideAfterSeconds > 0 && temporaryShow) {
      const timer = setTimeout(() => {
        setTemporaryShow(false);
      }, config.autoHideAfterSeconds * 1000);

      setAutoHideTimer(timer);
      return () => clearTimeout(timer);
    }
  }, [temporaryShow, config.isEnabled, config.autoHideAfterSeconds]);

  // Screenshot detection (visibility change)
  useEffect(() => {
    if (!config.hideOnScreenshot) return;

    const handleVisibilityChange = () => {
      if (document.hidden && config.isEnabled) {
        setTemporaryShow(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [config.hideOnScreenshot, config.isEnabled]);

  const toggleStealthMode = () => {
    setConfig((prev) => ({ ...prev, isEnabled: !prev.isEnabled }));
    setTemporaryShow(false);
  };

  const updateConfig = (updates: Partial<StealthModeConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const showValueTemporarily = (durationMs = 5000) => {
    setTemporaryShow(true);
    if (autoHideTimer) {
      clearTimeout(autoHideTimer);
    }
    const timer = setTimeout(() => {
      setTemporaryShow(false);
    }, durationMs);
    setAutoHideTimer(timer);
  };

  const formatHiddenValue = (value: string | number): string => {
    if (!config.isEnabled || temporaryShow) {
      return typeof value === 'number' ? value.toLocaleString() : value;
    }
    // Return masked value
    const strValue = String(value);
    if (strValue.includes('$')) {
      return '$••••••';
    }
    if (strValue.includes('%')) {
      return '••••%';
    }
    return '••••••';
  };

  const shouldHide = (field: keyof StealthModeConfig): boolean => {
    if (!config.isEnabled || temporaryShow) return false;
    return config[field] === true;
  };

  const isStealthMode = config.isEnabled && !temporaryShow;

  return (
    <StealthModeContext.Provider
      value={{
        config,
        isStealthMode,
        toggleStealthMode,
        updateConfig,
        showValueTemporarily,
        formatHiddenValue,
        shouldHide,
      }}
    >
      {children}
    </StealthModeContext.Provider>
  );
}

export function useStealthMode() {
  const context = useContext(StealthModeContext);
  if (!context) {
    throw new Error('useStealthMode must be used within a StealthModeProvider');
  }
  return context;
}
